import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchRequestTaskDetail,
  fetchRequestTaskListPage,
  RequestItem,
  TaskDetailResponse,
  TaskListItem,
  type CheckDetailKind,
} from "../../../api/requests";
import { fetchItCheckTaskDetail, fetchItCheckTaskListPage } from "../../../api/itCheck";
import { exportRequestDetailPdf } from "../exportRequestDetailPdf";
import { TASK_DETAIL_POLL_INTERVAL_MS, TASK_LIST_PAGE_SIZE } from "./requestDetailConstants";
import { mergeTaskListItems, sortMainTaskList } from "./requestDetailTaskListUtils";

interface RefreshTaskListOptions {
  resetSelection?: boolean;
  preserveSelectedTaskId?: string;
}

export const useRequestTasks = (requestId: string, kind: CheckDetailKind = "site") => {
  const [taskList, setTaskList] = useState<TaskListItem[] | null>(null);
  const [taskListLoading, setTaskListLoading] = useState(false);
  const [taskListCurrentPage, setTaskListCurrentPage] = useState(1);
  const [taskListTotalPages, setTaskListTotalPages] = useState(1);
  const [selectedTaskIndex, setSelectedTaskIndex] = useState(0);
  const [taskDetail, setTaskDetail] = useState<TaskDetailResponse | null>(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  const [taskDetailsCache, setTaskDetailsCache] = useState<Record<string, TaskDetailResponse>>({});
  const [deckViewportWidth, setDeckViewportWidth] = useState(0);

  const taskListPageRequestInFlightRef = useRef(false);
  const deckCardsRef = useRef<HTMLDivElement>(null);
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const lastForcedRefreshTaskIdRef = useRef<string | null>(null);

  const loadPage = useCallback(
    (page: number) =>
      kind === "it"
        ? fetchItCheckTaskListPage(requestId, page, TASK_LIST_PAGE_SIZE)
        : fetchRequestTaskListPage(requestId, page, TASK_LIST_PAGE_SIZE, "desc"),
    [kind, requestId]
  );

  const loadDetail = useCallback(
    (taskId: string) =>
      kind === "it"
        ? fetchItCheckTaskDetail(requestId, taskId)
        : fetchRequestTaskDetail(requestId, taskId),
    [kind, requestId]
  );

  const loadTaskListPage = useCallback(
    async ({ page, append }: { page: number; append?: boolean }): Promise<TaskListItem[] | null> => {
      if (!requestId || taskListPageRequestInFlightRef.current) return null;
      taskListPageRequestInFlightRef.current = true;
      if (!append) setTaskListLoading(true);
      try {
        const response = await loadPage(page);
        const items = sortMainTaskList(Array.isArray(response.items) ? response.items : []);
        setTaskListCurrentPage(response.page);
        setTaskListTotalPages(response.totalPages);
        setTaskList((prev) => {
          if (!append || !prev) return items;
          return mergeTaskListItems(prev, items);
        });
        setTaskDetailsCache((prev) => {
          if (append) return prev;
          const ids = new Set(items.map((task) => task.id));
          const next: Record<string, TaskDetailResponse> = {};
          Object.entries(prev).forEach(([taskId, detail]) => {
            if (ids.has(taskId)) next[taskId] = detail;
          });
          return next;
        });
        return items;
      } catch {
        if (!append) {
          setTaskList([]);
          setTaskDetail(null);
        }
        return null;
      } finally {
        taskListPageRequestInFlightRef.current = false;
        if (!append) setTaskListLoading(false);
      }
    },
    [loadPage, requestId]
  );

  const refreshTaskList = useCallback(
    async ({
      resetSelection = false,
      preserveSelectedTaskId,
    }: RefreshTaskListOptions = {}): Promise<TaskListItem[] | null> => {
      if (!requestId) return null;
      const items = await loadTaskListPage({ page: 1, append: false });
      if (!items) return null;
      if (items.length === 0) {
        setSelectedTaskIndex(0);
        setTaskDetail(null);
        return items;
      }
      if (resetSelection) {
        setSelectedTaskIndex(0);
        return items;
      }
      if (preserveSelectedTaskId) {
        const nextIndex = items.findIndex((task) => task.id === preserveSelectedTaskId);
        setSelectedTaskIndex(nextIndex >= 0 ? nextIndex : 0);
        return items;
      }
      setSelectedTaskIndex((prev) => Math.min(prev, items.length - 1));
      return items;
    },
    [loadTaskListPage, requestId]
  );

  const refreshTaskListRef = useRef(refreshTaskList);
  refreshTaskListRef.current = refreshTaskList;

  const syncTaskFromDetail = useCallback((taskId: string, detail: TaskDetailResponse) => {
    if (!detail.status) return;
    setTaskList((prev) => {
      if (!prev) return prev;
      return prev.map((task) =>
        task.id === taskId
          ? {
              ...task,
              status: detail.status ?? task.status,
              endDateTime: detail.endDateTime ?? task.endDateTime,
            }
          : task
      );
    });
  }, []);

  useEffect(() => {
    if (!requestId || !taskList || taskList.length === 0) {
      setTaskDetail(null);
      return;
    }
    const task = taskList[selectedTaskIndex];
    if (!task) {
      setTaskDetail(null);
      return;
    }
    const shouldForceRefreshTaskDetail =
      task.status === "new" || task.status === "in_progress";
    const cached = taskDetailsCache[task.id];
    const cacheStatusOutdated =
      Boolean(cached) &&
      !shouldForceRefreshTaskDetail &&
      cached?.status !== task.status;
    if (shouldForceRefreshTaskDetail && lastForcedRefreshTaskIdRef.current === task.id) {
      setTaskDetail(cached ?? null);
      setTaskDetailLoading(false);
      return;
    }
    if (shouldForceRefreshTaskDetail) {
      lastForcedRefreshTaskIdRef.current = task.id;
      setTaskDetail(cached ?? null);
    } else {
      lastForcedRefreshTaskIdRef.current = null;
    }
    if (cached && !shouldForceRefreshTaskDetail && !cacheStatusOutdated) {
      setTaskDetail(cached);
      setTaskDetailLoading(false);
      return;
    }
    setTaskDetailLoading(true);
    let cancelled = false;
    loadDetail(task.id)
      .then((detail) => {
        if (!cancelled) {
          setTaskDetail(detail);
          setTaskDetailsCache((prev) => ({ ...prev, [task.id]: detail }));
        }
      })
      .catch(() => {
        if (!cancelled) setTaskDetail(null);
      })
      .finally(() => {
        if (!cancelled) setTaskDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadDetail, requestId, taskList, selectedTaskIndex, taskDetailsCache]);

  useEffect(() => {
    if (!requestId || !taskList || taskList.length === 0) return;
    const task = taskList[selectedTaskIndex];
    if (!task) return;
    if (task.status !== "new" && task.status !== "in_progress") return;
    const timer = window.setInterval(() => {
      loadDetail(task.id)
        .then((detail) => {
          setTaskDetail(detail);
          setTaskDetailsCache((prev) => ({ ...prev, [task.id]: detail }));
          syncTaskFromDetail(task.id, detail);
        })
        .catch(() => {});
    }, TASK_DETAIL_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [loadDetail, requestId, selectedTaskIndex, syncTaskFromDetail, taskList]);

  useEffect(() => {
    const el = deckCardsRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0]?.contentRect ?? { width: 0 };
      setDeckViewportWidth(width);
    });
    ro.observe(el);
    setDeckViewportWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [taskList?.length]);

  const ensureTaskLoadedInMainList = useCallback(
    async (taskId: string): Promise<number> => {
      if (!requestId) return -1;
      const currentItems = taskList ?? [];
      const existingIndex = currentItems.findIndex((task) => task.id === taskId);
      if (existingIndex >= 0) return existingIndex;

      let merged = [...currentItems];
      let page = Math.max(taskListCurrentPage + 1, 2);
      let totalPages = taskListTotalPages;

      while (page <= totalPages) {
        const response = await loadPage(page);
        const pageItems = sortMainTaskList(Array.isArray(response.items) ? response.items : []);
        totalPages = response.totalPages;
        const seen = new Set(merged.map((task) => task.id));
        pageItems.forEach((task) => {
          if (!seen.has(task.id)) {
            seen.add(task.id);
            merged.push(task);
          }
        });
        setTaskList(merged);
        setTaskListCurrentPage(response.page);
        setTaskListTotalPages(response.totalPages);
        const foundIndex = merged.findIndex((task) => task.id === taskId);
        if (foundIndex >= 0) return foundIndex;
        page = response.page + 1;
      }

      return -1;
    },
    [loadPage, requestId, taskList, taskListCurrentPage, taskListTotalPages]
  );

  const handlePrevTask = useCallback(() => {
    setSelectedTaskIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleNextTask = useCallback(async () => {
    if (!taskList || taskList.length === 0) return;
    if (selectedTaskIndex < taskList.length - 1) {
      setSelectedTaskIndex((prev) => prev + 1);
      return;
    }
    if (taskListCurrentPage >= taskListTotalPages) return;
    const nextPageItems = await loadTaskListPage({
      page: taskListCurrentPage + 1,
      append: true,
    });
    if (nextPageItems && nextPageItems.length > 0) {
      setSelectedTaskIndex((prev) => prev + 1);
    }
  }, [loadTaskListPage, selectedTaskIndex, taskList, taskListCurrentPage, taskListTotalPages]);

  const handleDownloadPdf = useCallback(
    async (data: RequestItem | null) => {
      if (!data || !taskDetail || !pdfContentRef.current) return;
      await exportRequestDetailPdf({
        data,
        taskDetail,
        pdfContent: pdfContentRef.current,
      });
    },
    [taskDetail]
  );

  const hasNewTask = Boolean(taskList?.some((task) => task.status === "new"));
  const hasInProgressTask = Boolean(taskList?.some((task) => task.status === "in_progress"));
  const hasTaskItems = Array.isArray(taskList) && taskList.length > 0;

  return {
    taskList,
    taskListLoading,
    taskListCurrentPage,
    taskListTotalPages,
    selectedTaskIndex,
    setSelectedTaskIndex,
    taskDetail,
    taskDetailLoading,
    taskDetailsCache,
    deckViewportWidth,
    deckCardsRef,
    pdfContentRef,
    hasNewTask,
    hasInProgressTask,
    hasTaskItems,
    refreshTaskList,
    refreshTaskListRef,
    ensureTaskLoadedInMainList,
    handlePrevTask,
    handleNextTask,
    handleDownloadPdf,
  };
};
